import useDetachedUploadRefresh from './useDetachedUploadRefresh';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/api';
import { getPrivateCacheScope, isCurrentPrivateCacheScope, subscribePrivateCacheScope } from '../utils/privateCacheScope';
import { getPhotoVersion } from '../utils/photoCache';
import { isProcessingLike } from '../utils/processingState';

export default function useArchiveFolderImages(folderId) {
  const [scope, setScope] = useState(getPrivateCacheScope);
  const [snapshot, setSnapshot] = useState(null);
  const target = `${folderId}:${scope.generation}`;
  const currentTarget = useRef(target); currentTarget.current = target;
  const mounted = useRef(false);
  const data = snapshot?.target === target ? snapshot.data : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const request = useRef(0);
  useEffect(() => subscribePrivateCacheScope(setScope), []);
  const load = useCallback(async () => {
    const identity = scope;
    if (!mounted.current || currentTarget.current !== target || !isCurrentPrivateCacheScope(identity)) return;
    const sequence = ++request.current;
    try {
      const res = await apiFetch(`/api/archive/folders/${folderId}/images`, { cache: 'no-store' });
      if (!res.ok) throw Object.assign(new Error('Could not refresh this folder. Please try again.'), { status: res.status });
      const result = await res.json();
      if (sequence !== request.current || !isCurrentPrivateCacheScope(identity)) return;
      setSnapshot({ target, data: { ...result, images: (result.images || []).map(image => ({
        ...image,
        // Processing completion changes unsigned preview URLs; signed URLs remain untouched.
        _v: `${getPhotoVersion(image)}:${image.processing_status || 'ready'}`
      })) } }); setError(null); setUnavailable(false);
    } catch (err) {
      if (sequence === request.current && isCurrentPrivateCacheScope(identity)) {
        const denied = [401, 403, 404].includes(err.status);
        setUnavailable(denied);
        if (denied) setSnapshot(null);
        setError(denied ? 'This folder is unavailable. It may have been removed or you may not have access.' : 'Could not refresh this folder. Please try again.');
      }
    } finally {
      if (sequence === request.current && isCurrentPrivateCacheScope(identity)) setLoading(false);
    }
  }, [folderId, scope.generation, target]);
  useEffect(() => {
    mounted.current = true;
    setSnapshot(null); setLoading(true); setError(null); setUnavailable(false); load();
    return () => { mounted.current = false; request.current++; };
  }, [load]);
  useDetachedUploadRefresh(detail => String(detail.archiveFolderId) === String(folderId), () => load());
  const processing = (data?.images || []).some(isProcessingLike);
  useEffect(() => {
    if (!processing) return;
    // Recursive scheduling waits for each response; no overlapping poll requests.
    let cancelled = false; let timer;
    const poll = async () => { await load(); if (!cancelled) timer = setTimeout(poll, 1500); };
    timer = setTimeout(poll, 1500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [processing, load]);
  return { data, loading, error, unavailable, load };
}
